<template>
  <div class="cloudflare-video-preview" :class="{ 'fullscreen': isFullscreen }">
    <div class="video-container">
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
      </div>

      <!-- Error message -->
      <div v-if="hasError" class="error-message">
        <span>{{ errorMessage || 'Failed to load video' }}</span>
      </div>

      <!-- Video player -->
      <video
        v-show="!hasError"
        ref="videoRef"
        class="video-element"
        :poster="poster"
        :muted="muted"
        :loop="loop"
        :controls="showControls"
        :autoplay="autoplay"
        @error="handleMediaError"
        @loadedmetadata="handleMediaLoaded"
        @click="togglePlay"
      >
        Your browser does not support HTML5 video.
      </video>

      <!-- Play/pause overlay -->
      <div 
        v-if="!isLoading && !hasError && !isPlaying && !showControls" 
        class="play-overlay"
        @click="togglePlay"
      >
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <polygon points="5 3 19 12 5 21 5 3"></polygon>
        </svg>
      </div>

      <!-- Video controls (custom controls when showControls is false) -->
      <div 
        v-if="!isLoading && !hasError && !showControls" 
        class="video-custom-controls"
        :class="{ 'visible': controlsVisible }"
      >
        <button class="control-btn" @click.stop="togglePlay">
          <svg v-if="isPlaying" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <rect x="6" y="4" width="4" height="16"></rect>
            <rect x="14" y="4" width="4" height="16"></rect>
          </svg>
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </button>

        <div class="progress-container" @click.stop="handleProgressClick">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: `${progress}%` }"></div>
            <div class="progress-handle" :style="{ left: `${progress}%` }"></div>
          </div>
          <div class="time-display">
            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
          </div>
        </div>

        <button class="control-btn" @click.stop="toggleMute">
          <svg v-if="muted" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
            <line x1="23" y1="9" x2="17" y2="15"></line>
            <line x1="17" y1="9" x2="23" y2="15"></line>
          </svg>
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
          </svg>
        </button>

        <button class="control-btn" @click.stop="toggleFullscreen">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
          </svg>
        </button>
      </div>

      <!-- Fullscreen button (when using default HTML5 controls) -->
      <div v-if="showControls && !isFullscreen && !isLoading && !hasError" class="fullscreen-container">
        <button class="fullscreen-btn" @click.stop="toggleFullscreen" title="Toggle fullscreen">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
          </svg>
        </button>
      </div>
      
      <!-- Close fullscreen button -->
      <button v-if="isFullscreen" class="close-btn" @click.stop="closeFullscreen" title="Exit fullscreen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- Video metadata (optional) -->
      <div v-if="showMetadata && videoMetadata" class="video-metadata">
        <p>Duration: {{ formatTime(videoMetadata.duration) }}</p>
        <p v-if="videoMetadata.width && videoMetadata.height">Resolution: {{ videoMetadata.width }}x{{ videoMetadata.height }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import Hls from 'hls.js';

export default {
  name: 'CloudflareVideoPreview',
  props: {
    src: {
      type: String,
      required: true
    },
    poster: {
      type: String,
      default: ''
    },
    width: {
      type: [Number, String],
      default: null
    },
    height: {
      type: [Number, String],
      default: null
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    muted: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    showControls: {
      type: Boolean,
      default: true
    },
    showMetadata: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const videoRef = ref(null);
    const isLoading = ref(true);
    const hasError = ref(false);
    const errorMessage = ref('');
    const isFullscreen = ref(false);
    const isPlaying = ref(false);
    const controlsVisible = ref(false);
    const controlsTimeout = ref(null);
    const videoMetadata = ref(null);
    const hls = ref(null);
    const currentTime = ref(0);
    const duration = ref(0);
    const progress = ref(0);
    
    // Computed properties
    const isHlsStream = computed(() => {
      const url = props.src.toLowerCase();
      return url.endsWith('.m3u8') || url.includes('manifest') || url.includes('playlist');
    });

    // Methods
    const initVideo = () => {
      if (!videoRef.value) return;
      
      const video = videoRef.value;
      
      if (isHlsStream.value && Hls.isSupported()) {
        // HLS stream handling with hls.js
        if (hls.value) {
          hls.value.destroy();
        }
        
        hls.value = new Hls({
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
        });
        
        hls.value.attachMedia(video);
        
        hls.value.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.value.loadSource(props.src);
        });
        
        hls.value.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error('HLS network error', data);
                hls.value.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('HLS media error', data);
                hls.value.recoverMediaError();
                break;
              default:
                handleMediaError({ type: 'HLS Error', message: data.details });
                break;
            }
          }
        });

        hls.value.on(Hls.Events.MANIFEST_PARSED, () => {
          if (props.autoplay) {
            video.play().catch(e => console.error('Auto-play prevented:', e));
          }
        });
      } else {
        // Regular video handling
        video.src = props.src;
        
        if (props.autoplay) {
          video.play().catch(e => console.error('Auto-play prevented:', e));
        }
      }

      // Add event listeners for custom controls
      video.addEventListener('timeupdate', updateProgress);
      video.addEventListener('play', () => { isPlaying.value = true; });
      video.addEventListener('pause', () => { isPlaying.value = false; });
    };
    
    const handleMediaLoaded = () => { 
      isLoading.value = false;
      hasError.value = false;
      
      const video = videoRef.value;
      
      if (video) {
        duration.value = video.duration;
        
        videoMetadata.value = {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        };
        
        emit('loaded', {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          element: video,
          src: props.src
        });
      }
    };
    
    const handleMediaError = (event) => {
      isLoading.value = false;
      hasError.value = true;
      errorMessage.value = `Failed to load video: ${event.type || 'Unknown error'}`;
      emit('error', { message: errorMessage.value, src: props.src });
    };
    
    const togglePlay = () => {
      const video = videoRef.value;
      if (!video) return;
      
      if (video.paused) {
        video.play().catch(e => console.error('Play prevented:', e));
      } else {
        video.pause();
      }
      
      showControls();
    };
    
    const toggleMute = () => {
      const video = videoRef.value;
      if (!video) return;
      video.muted = !video.muted;
      showControls();
    };
    
    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value;
      
      // Add/remove body class to prevent scrolling
      if (isFullscreen.value) {
        document.body.classList.add('video-viewer-open');
      } else {
        document.body.classList.remove('video-viewer-open');
      }
      
      emit('fullscreen-change', isFullscreen.value);
    };
    
    const closeFullscreen = () => {
      isFullscreen.value = false;
      document.body.classList.remove('video-viewer-open');
      emit('fullscreen-change', false);
    };
    
    const updateProgress = () => {
      const video = videoRef.value;
      if (!video) return;
      
      currentTime.value = video.currentTime;
      duration.value = video.duration;
      progress.value = (video.currentTime / video.duration) * 100;
    };
    
    const handleProgressClick = (event) => {
      const video = videoRef.value;
      if (!video) return;
      
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = x / rect.width;
      
      video.currentTime = percentage * video.duration;
      progress.value = percentage * 100;
      
      showControls();
    };
    
    const showControls = () => {
      controlsVisible.value = true;
      
      clearTimeout(controlsTimeout.value);
      controlsTimeout.value = setTimeout(() => {
        controlsVisible.value = false;
      }, 3000);
    };
    
    const formatTime = (seconds) => {
      if (!seconds || isNaN(seconds)) return '0:00';
      
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Handle mouse movement for showing controls
    const handleMouseMove = () => {
      if (!props.showControls) {
        showControls();
      }
    };
    
    // Handle escape key for exiting fullscreen
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFullscreen.value) {
        closeFullscreen();
      }
      
      // Space bar for play/pause
      if (event.key === ' ' && document.activeElement === document.body) {
        togglePlay();
        event.preventDefault();
      }
    };
    
    // Lifecycle hooks
    onMounted(() => {
      window.addEventListener('keydown', handleKeyDown);
      initVideo();
      
      const videoContainer = document.querySelector('.cloudflare-video-preview');
      if (videoContainer) {
        videoContainer.addEventListener('mousemove', handleMouseMove);
      }
    });
    
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeyDown);
      
      const videoContainer = document.querySelector('.cloudflare-video-preview');
      if (videoContainer) {
        videoContainer.removeEventListener('mousemove', handleMouseMove);
      }
      
      document.body.classList.remove('video-viewer-open');
      
      if (hls.value) {
        hls.value.destroy();
      }
      
      const video = videoRef.value;
      if (video) {
        video.removeEventListener('timeupdate', updateProgress);
        video.pause();
      }
      
      clearTimeout(controlsTimeout.value);
    });
    
    // Watch for changes to src
    watch(() => props.src, (newSrc, oldSrc) => {
      if (newSrc !== oldSrc) {
        isLoading.value = true;
        hasError.value = false;
        errorMessage.value = '';
        
        // Re-initialize video with new source
        setTimeout(() => initVideo(), 0);
      }
    });
    
    return {
      videoRef,
      isLoading,
      hasError,
      errorMessage,
      isFullscreen,
      isPlaying,
      controlsVisible,
      videoMetadata,
      currentTime,
      duration,
      progress,
      togglePlay,
      toggleMute,
      toggleFullscreen,
      closeFullscreen,
      handleProgressClick,
      handleMediaLoaded,
      handleMediaError,
      formatTime
    };
  }
};
</script>

<style scoped>
.cloudflare-video-preview {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #111827;
}

.cloudflare-video-preview.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  border-radius: 0;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-element {
  width: 100%;
  height: auto;
  max-height: 300px;
  display: block;
  cursor: pointer;
}

.fullscreen .video-element {
  max-height: 90vh;
  max-width: 90vw;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-left-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-message {
  color: #ef4444;
  padding: 10px;
  text-align: center;
  border: 1px solid #ef4444;
  border-radius: 4px;
  background-color: #fef2f2;
  width: 80%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;
}

.cloudflare-video-preview:hover .play-overlay {
  opacity: 1;
}

.video-custom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 10px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-custom-controls.visible {
  opacity: 1;
}

.cloudflare-video-preview:hover .video-custom-controls {
  opacity: 1;
}

.control-btn {
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.control-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.progress-container {
  flex: 1;
  margin: 0 10px;
  cursor: pointer;
}

.progress-bar {
  height: 5px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  position: relative;
}

.progress-fill {
  height: 100%;
  background-color: #3b82f6;
  border-radius: 2px;
  position: absolute;
}

.progress-handle {
  width: 12px;
  height: 12px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.time-display {
  font-size: 12px;
  color: white;
  margin-top: 5px;
  text-align: right;
}

.fullscreen-container {
  position: absolute;
  top: 10px;
  right: 10px;
}

.fullscreen-btn,
.close-btn {
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: background-color 0.2s ease;
}

.fullscreen-btn:hover,
.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

.video-metadata {
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 12px;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 5px 10px;
  border-radius: 4px;
  pointer-events: none;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 